import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Button, Card, message, Typography, Select, Space, Modal, Alert } from "antd";
import { UploadOutlined, DownloadOutlined, InfoCircleOutlined, FileExcelOutlined, UndoOutlined, ImportOutlined } from "@ant-design/icons";
import ImportExportServ from "../../service/ImportExportServ";
import ShopServ from "../../service/ShopServ";
import ExportServ from "../../service/ExportServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import Notify from "../../util/Notify";

const { Title, Text } = Typography;

const SELLER_ENTITIES = [
    { label: 'Products', value: 'Product', fields: ['_id', 'name', 'description', 'price', 'mrp', 'discount', 'stock', 'imageUrl', 'images', 'active', 'shopId', 'categoryId', 'subCategoryId', 'brandId', 'approvalStatus', 'options'] },
    { label: 'Product Options (Detailed Update)', value: 'ProductOption', fields: ['productId', 'productName', 'optionGroup', 'variantValue', 'variantPrice', 'variantMrp', 'variantStock', 'variantSku', 'variantImage'] },
    { label: 'Orders', value: 'Order' }
];

const ADMIN_ENTITIES = [
    ...SELLER_ENTITIES,
    { label: 'Categories (Parents)', value: 'Category', fields: ['_id', 'name', 'description', 'imageUrl', 'isActive'] },
    { label: 'Sub Categories', value: 'SubCategory', fields: ['_id', 'name', 'parentId', 'description', 'imageUrl', 'isActive'] },
    { label: 'Shops', value: 'Shop', fields: ['_id', 'shopId', 'name', 'description', 'imageUrl', 'isActive'] },
    { label: 'Brands', value: 'Brand', fields: ['_id', 'name', 'description', 'imageUrl', 'isActive'] },
    { label: 'Users', value: 'User', fields: ['_id', 'firstName', 'lastName', 'email', 'phone', 'role', 'isActive'] }
];

const ImportData = () => {
    const [uploading, setUploading] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState('Product');
    const [isAuthorized, setIsAuthorized] = useState(true);
    const [importErrors, setImportErrors] = useState([]);
    const [authChecking, setAuthChecking] = useState(true);
    const navigate = useNavigate();

    const role = localStorage.getItem("role");
    const ENTITIES = (role === "admin" || role === "superadmin") ? ADMIN_ENTITIES : SELLER_ENTITIES;

    useEffect(() => {
        checkShopAuth();
    }, []);

    const checkShopAuth = async () => {
        const currentRole = localStorage.getItem("role");
        if (currentRole !== 'seller') {
            setIsAuthorized(true);
            setAuthChecking(false);
            return;
        }

        try {
            const shopsRes = await ShopServ.getSellerShops();
            const shops = shopsRes?.data || [];
            const activeShops = shops.filter(s => s.isActive);
            setIsAuthorized(activeShops.length > 0);
        } catch (error) {
            console.error("Auth check failed", error);
            setIsAuthorized(false);
        } finally {
            setAuthChecking(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            setUploading(true);
            const entityConfig = ENTITIES.find(e => e.value === selectedEntity);
            const defaultFields = ['_id', 'name', 'shopId', 'categoryId', 'brandId', 'price', 'stock', 'active'];

            const payload = {
                entity: selectedEntity,
                fields: entityConfig?.fields || defaultFields,
                format: 'excel',
                filters: {}
            };

            const response = await ExportServ.exportData(payload);

            // Trigger Download with correct MIME type to prevent corruption
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${selectedEntity}_Import_Template.xlsx`);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
                document.body.removeChild(link);
            }, 100);

            Notify.success("Template downloaded!", "Fill the data and upload it back to sync your store.");
        } catch (error) {
            const msg = (typeof error === 'string') ? error : (error?.message || "Failed to generate template. Please try again.");
            Notify.error(msg);
        } finally {
            setUploading(false);
        }
    };

    const handleImport = async (file) => {
        try {
            setUploading(true);
            setImportErrors([]);
            const formData = new FormData();
            formData.append("file", file);

            const result = await ImportExportServ.importEntity(selectedEntity, formData);

            if (result.success) {
                const { success, updated, failed, errors } = result.summary;

                if (failed > 0) {
                    Notify.warning(
                        "Import Partial Success",
                        `${selectedEntity} data: ${success} new, ${updated} updated, but ${failed} records FAILED.`
                    );

                    Modal.error({
                        title: 'Import Errors Found',
                        width: 600,
                        content: (
                            <div className="pcu-max-h-400 pcu-overflow-y-auto">
                                <Text type="danger">The following rows could not be processed:</Text>
                                <ul className="pcu-margin-top-10">
                                    {errors.map((err, i) => <li key={i}>{err}</li>)}
                                </ul>
                            </div>
                        )
                    });
                    setImportErrors(errors);
                } else {
                    Notify.success(
                        "Import Successful",
                        `${selectedEntity} data processed: ${success} new records created, ${updated} records updated.`
                    );
                }
            } else {
                Notify.error("Import Blocked", result.message || "Generic validation error occurred.");
                if (result.summary?.errors?.length > 0) {
                    setImportErrors(result.summary.errors);
                    Modal.error({
                        title: 'Import Validation Failed',
                        width: 600,
                        content: (
                            <div className="pcu-max-h-400 pcu-overflow-y-auto">
                                <Text type="danger">The following errors must be fixed before data can be imported:</Text>
                                <ul className="pcu-margin-top-10">
                                    {result.summary.errors.map((err, i) => <li key={i}>{err}</li>)}
                                </ul>
                            </div>
                        )
                    });
                }
            }
        } catch (error) {
            console.error(error);
            const msg = (typeof error === 'string') ? error : (error?.message || "Import failed. Please check your IDs and file format.");
            Notify.error(msg);
        } finally {
            setUploading(false);
        }
        return false;
    };

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs items={[{ label: 'Data Management' }, { label: 'Universal Importer' }]} />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-20 pcu-shadow-sm pcu-overflow-hidden" style={{ border: 'none', padding: '32px' }}>
                {/* Dashboard Style Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <div style={{ 
                            width: '56px', 
                            height: '56px', 
                            background: '#eff6ff', 
                            borderRadius: '16px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center' 
                        }}>
                            <ImportOutlined style={{ fontSize: '26px', color: '#3b82f6' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 800, color: '#1e293b' }}>Universal Data Importer</h1>
                            <Text type="secondary" style={{ fontSize: '13px' }}>Manage your store collection via optimized bulk operations</Text>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <Button icon={<UndoOutlined />} onClick={() => window.location.reload()}>Refresh</Button>
                    </div>
                </div>

                <div style={{ maxWidth: '950px', margin: '0 auto' }} className={`${isAuthorized ? '' : 'pcu-opacity-60 pcu-pointer-none'}`}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '32px' }}>
                        {/* Left Side: Upload area */}
                        <div style={{ 
                            padding: '32px', 
                            borderRadius: '24px', 
                            background: '#f0fdf4', 
                            border: '1px dashed #22c55e', 
                            boxShadow: '0 4px 20px rgba(34, 197, 94, 0.05)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
                                <div style={{ 
                                    background: '#22c55e', 
                                    color: '#fff', 
                                    width: '40px', 
                                    height: '40px', 
                                    borderRadius: '12px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontWeight: 800,
                                    fontSize: '18px',
                                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.2)'
                                }}>
                                    <UploadOutlined />
                                </div>
                                <div>
                                    <Title level={4} style={{ margin: 0, fontWeight: 700 }}>Upload & Process</Title>
                                    <Text type="secondary" style={{ fontSize: '13px' }}>Sync data back to the store</Text>
                                </div>
                            </div>

                            <div style={{ background: '#fff', padding: '20px', borderRadius: '18px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>TARGET COLLECTION</label>
                                <Select
                                    className="pcu-w-100 ss-select"
                                    value={selectedEntity}
                                    onChange={setSelectedEntity}
                                    size="large"
                                    dropdownStyle={{ borderRadius: '12px' }}
                                >
                                    {ENTITIES.map(e => <Select.Option key={e.value} value={e.value}>{e.label}</Select.Option>)}
                                </Select>
                            </div>

                            <div style={{ background: '#fff', borderRadius: '20px', border: '2px dashed #bbf7d0', overflow: 'hidden' }}>
                                <Upload.Dragger 
                                    beforeUpload={handleImport} 
                                    showUploadList={false} 
                                    disabled={!selectedEntity}
                                    style={{ background: 'transparent', border: 'none' }}
                                >
                                    <div style={{ padding: '40px 20px' }}>
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ 
                                        width: '56px', 
                                        height: '56px', 
                                        background: '#f0fdf4', 
                                        borderRadius: '50%', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        margin: '0 auto',
                                        marginBottom: '12px'
                                    }}>
                                        <FileExcelOutlined style={{ color: '#22c55e', fontSize: '24px' }} />
                                    </div>
                                    <p style={{ margin: 0, fontWeight: 700, fontSize: '16px', color: '#1e293b' }}>Drop file to sync</p>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                                        {uploading ? 'Processing...' : `Target: ${selectedEntity}`}
                                    </p>
                                </div>
                                    <Button size="middle" style={{ borderRadius: '8px', fontWeight: 700 }}>Select File</Button>
                                    </div>
                                </Upload.Dragger>
                            </div>

                            {importErrors.length > 0 && (
                                <div style={{ marginTop: '24px', padding: '20px', background: '#fff1f2', borderRadius: '16px', border: '1px solid #fecdd3' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '8px', color: '#be123c', fontWeight: 700 }}>
                                        <InfoCircleOutlined />
                                        <span>Failures ({importErrors.length})</span>
                                    </div>
                                    <div style={{ maxHeight: '150px', overflowY: 'auto', fontSize: '12px', color: '#9f1239' }}>
                                        <ul style={{ paddingLeft: '18px', margin: 0 }}>
                                            {importErrors.map((err, i) => <li key={i} style={{ marginBottom: '4px' }}>{err}</li>)}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Side: Sync Logic vertical list */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center' }}>
                            <Title level={5} style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#1e293b', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                <InfoCircleOutlined style={{ color: '#22c55e', marginRight: '8px' }} /> Smart Sync Logic
                            </Title>
                            {[
                                { t: 'Auto-Update', d: 'Existing records are accurately patched based on unique IDs.', c: '#eff6ff', ic: '#3b82f6' },
                                { t: 'Auto-Create', d: 'Rows without IDs are automatically added as new records.', c: '#f0fdf4', ic: '#22c55e' },
                                { t: 'Secure Link', d: 'All category and shop relationships are strictly validated.', c: '#faf5ff', ic: '#a855f7' },
                                { t: 'Multi-Format', d: 'Supports high-speed ingestion of XLSX, XLS, and CSV files.', c: '#fff7ed', ic: '#f97316' }
                            ].map((rule, idx) => (
                                <div key={idx} style={{ 
                                    padding: '20px', 
                                    background: '#fff', 
                                    borderRadius: '16px', 
                                    border: '1px solid #f1f5f9',
                                    display: 'flex',
                                    gap: '16px',
                                    alignItems: 'flex-start',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
                                }}>
                                    <div style={{ 
                                        width: '32px', 
                                        height: '32px', 
                                        background: rule.c, 
                                        borderRadius: '8px', 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        marginTop: '2px'
                                    }}>
                                        <div style={{ width: '6px', height: '6px', background: rule.ic, borderRadius: '50%' }} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 800, fontSize: '14px', color: '#1e293b', marginBottom: '2px' }}>{rule.t}</div>
                                        <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.4' }}>{rule.d}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportData;
